import { ApiRequestError, apiBaseUrl, apiClient, apiErrorFromResponse, reportedApiError } from "@/shared/api/client";
import { getAuthToken } from "@/shared/api/auth";

export interface GameListItem {
  id: string;
  opponent: string;
  opponentRating: number;
  playerRating: number;
  playerColor: "w" | "b";
  result: string;
  timeControl: string;
  eco: string | null;
  openingName: string | null;
  playedAt: string;
  startedAt: string | null;
  durationSeconds: number | null;
  exactTimeControl: string | null;
  rated: boolean | null;
  rules: string;
  termination: string | null;
  resultCode: string | null;
  chessComUrl: string | null;
  tournamentUrl: string | null;
  ratingChange: number | null;
  analyzed: boolean;
  analysisDepth: number | null;
  analysisPreset?: string | null;
  accuracy: number | null;
  chessComAccuracy: number | null;
  engineAccuracy: number | null;
  estimatedRating: number | null;
  averageCpl: number | null;
  blunderCount: number | null;
  mistakeCount: number | null;
}

export interface ImportGamesResult {
  playerId: string;
  gamesImported: number;
}

export interface ImportProgress {
  imported: number;
  target: number | null;
  phase: "finding" | "bringing" | "complete" | "stopped";
}

export interface GameLibrarySummary {
  totalGames: number;
  totalAnalyzedGames: number;
  formats: Array<{ timeControl: string; count: number; analyzedCount: number }>;
  clocks: Array<{ timeControl: string; exactTimeControl: string | null; count: number; analyzedCount: number }>;
}

export interface GameListFilters {
  sortBy?: "playedAt" | "opponent" | "result" | "timeControl" | "analyzed";
  sortDirection?: "asc" | "desc";
  analyzed?: boolean;
  timeControl?: string;
  exactTimeControl?: string;
  period?: string;
  from?: string;
  to?: string;
  search?: string;
}

export interface GameStreamProgress {
  loaded: number;
  total: number;
}

export interface GameStreamHandlers {
  onProgress: (progress: GameStreamProgress) => void | Promise<void>;
  onGames: (games: GameListItem[], progress: GameStreamProgress) => void | Promise<void>;
}

export const fetchGames = (
  limit = 200,
  offset = 0,
  filters: GameListFilters = {},
  signal?: AbortSignal,
): Promise<GameListItem[]> => {
  const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  if (filters.analyzed !== undefined) params.set("analyzed", String(filters.analyzed));
  if (filters.timeControl) params.set("timeControl", filters.timeControl);
  if (filters.exactTimeControl) params.set("exactTimeControl", filters.exactTimeControl);
  if (filters.period) params.set("period", filters.period);
  if (filters.from) params.set("from", filters.from);
  if (filters.to) params.set("to", filters.to);
  if (filters.sortBy) params.set("sortBy", filters.sortBy);
  if (filters.sortDirection) params.set("sortDirection", filters.sortDirection);
  if (filters.search) params.set("search", filters.search);
  return apiClient<GameListItem[]>(`/games?${params.toString()}`, { signal });
};

export const deleteGames = (gameIds: string[]): Promise<{ deletedGameIds: string[] }> =>
  apiClient("/games", { method: "DELETE", body: JSON.stringify({ gameIds }) });

export const fetchGameLibrarySummary = (signal?: AbortSignal): Promise<GameLibrarySummary> =>
  apiClient<GameLibrarySummary>("/games/summary", { signal });

export const fetchGame = (gameId: string): Promise<GameListItem | null> =>
  apiClient<GameListItem | null>(`/games/${gameId}`);

export const streamGames = async (
  filters: GameListFilters,
  handlers: GameStreamHandlers,
  signal?: AbortSignal,
): Promise<void> => {
  const params = new URLSearchParams();
  if (filters.analyzed !== undefined) params.set("analyzed", String(filters.analyzed));
  if (filters.timeControl) params.set("timeControl", filters.timeControl);
  if (filters.exactTimeControl) params.set("exactTimeControl", filters.exactTimeControl);
  if (filters.period) params.set("period", filters.period);
  if (filters.from) params.set("from", filters.from);
  if (filters.to) params.set("to", filters.to);

  const token = await getAuthToken();
  const response = await fetch(`${apiBaseUrl}/games/stream?${params.toString()}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    signal,
  });

  if (!response.ok || !response.body) {
    let message = "Unable to stream games";
    try {
      const body = (await response.json()) as { error?: { message?: string; code?: string } };
      message = body.error?.message ?? message;
    } catch {
      // The endpoint normally returns JSON errors before opening the stream.
    }
    throw reportedApiError(message, "STREAM_FAILED", response.status);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let completed = false;

  const handleEvent = async (rawEvent: string) => {
    let event = "message";
    const data: string[] = [];
    for (const line of rawEvent.split("\n")) {
      if (line.startsWith("event:")) event = line.slice(6).trim();
      if (line.startsWith("data:")) data.push(line.slice(5).trimStart());
    }
    if (data.length === 0) return;

    const payload = JSON.parse(data.join("\n")) as
      GameStreamProgress | { games: GameListItem[]; loaded: number; total: number } | { message: string };
    if (event === "progress") {
      await handlers.onProgress(payload as GameStreamProgress);
    } else if (event === "games") {
      const gamesPayload = payload as { games: GameListItem[]; loaded: number; total: number };
      await handlers.onGames(gamesPayload.games, {
        loaded: gamesPayload.loaded,
        total: gamesPayload.total,
      });
    } else if (event === "complete") {
      completed = true;
    } else if (event === "error") {
      throw reportedApiError((payload as { message: string }).message, "STREAM_FAILED", response.status);
    }
  };

  while (true) {
    const { done, value } = await reader.read();
    buffer += decoder.decode(value, { stream: !done }).replace(/\r\n/g, "\n");

    let boundary = buffer.indexOf("\n\n");
    while (boundary >= 0) {
      await handleEvent(buffer.slice(0, boundary));
      buffer = buffer.slice(boundary + 2);
      boundary = buffer.indexOf("\n\n");
    }
    if (done) break;
  }

  if (buffer.trim()) await handleEvent(buffer);
  if (!completed) {
    throw new ApiRequestError("Game stream ended before completion", "STREAM_INCOMPLETE", response.status);
  }
};

/**
 * Imports the current user's games from Chess.com. No username param —
 * the backend uses the username already stored on the player profile
 * (the same one provided at signup), since Chess.com's public API only
 * ever needs a username, never OAuth.
 */
export const importChessComGames = (limit?: number): Promise<ImportGamesResult> => {
  const url = limit === undefined ? "/chess-com/import" : `/chess-com/import?limit=${limit}`;
  return apiClient<ImportGamesResult>(url, { method: "POST" });
};

export interface ChessComImportFilters {
  from?: string;
  to?: string;
  timeControl?: string;
  exactTimeControl?: string;
}

export const streamChessComImport = async (
  limit: number | "all",
  signal: AbortSignal,
  onProgress: (progress: ImportProgress) => void,
  filters: ChessComImportFilters = {},
): Promise<ImportProgress> => {
  const token = await getAuthToken();
  const response = await fetch(`${apiBaseUrl}/chess-com/import/stream`, {
    method: "POST",
    signal,
    headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify({ limit, ...filters }),
  });
  if (!response.ok) throw await apiErrorFromResponse(response);
  if (!response.body) throw reportedApiError("Unable to import games", "IMPORT_FAILED", response.status);
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let finalProgress: ImportProgress = { imported: 0, target: limit === "all" ? null : limit, phase: "finding" };
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const events = buffer.split("\n\n");
    buffer = events.pop() ?? "";
    for (const raw of events) {
      const event = raw.match(/^event: (.+)$/m)?.[1];
      const data = raw.match(/^data: (.+)$/m)?.[1];
      if (!event || !data) continue;
      const progress = JSON.parse(data) as ImportProgress | { message: string };
      if (event === "error")
        throw reportedApiError((progress as { message: string }).message, "IMPORT_FAILED", response.status);
      finalProgress = progress as ImportProgress;
      onProgress(finalProgress);
    }
  }
  return finalProgress;
};
